import React, {
	createContext,
	useContext,
	useState,
	ReactNode,
	useRef,
} from "react";
import styled from "styled-components";

const ModalProviderWrapper = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	z-index: 100002;
	flex-grow: initial;
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const ModalOverlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: rgba(0, 0, 0, 0.7);
	backdrop-filter: blur(30px);
	z-index: 100001;
`;

type ModalContextType = {
	openModal: (component: ReactNode) => void;
	closeModal: () => void;
	isOpen: boolean;
};

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = () => {
	const context = useContext(ModalContext);
	if (!context) {
		throw new Error("useModal must be used within a ModalProvider");
	}
	return context;
};

export const ModalProvider = ({ children }: { children: ReactNode }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [modalComponent, setModalComponent] = useState<ReactNode | null>(null);
	const modalRef = useRef<HTMLDivElement>(null);

	const openModal = (component: ReactNode) => {
		setModalComponent(component);
		setIsOpen(true);
	};

	const closeModal = () => {
		setIsOpen(false);
		setModalComponent(null);
	};

	return (
		<ModalContext.Provider value={{ openModal, closeModal, isOpen }}>
			{children}
			{isOpen && (
				<>
					<ModalOverlay />
					<ModalProviderWrapper ref={modalRef}>
						{modalComponent}
					</ModalProviderWrapper>
				</>
			)}
		</ModalContext.Provider>
	);
};
